/**
 * 이상한 방 - 봉투 화면을 앉히는 일 ("무엇을 그리는가")
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스) · 3.6절(확률은 표 하나에서 화면으로) · 0절 판정표 10번
 *
 * `envelopePhases.ts`에서 갈라져 나왔다. 그 파일의 이름은 **"언제"**인데, 첫 상태를 앉히는 일(`resetVisual`)과
 * 문구를 채우는 일(`paintTexts`)은 "언제"가 아니라 "무엇을"이다. 릴이 들어오면서 지휘자가 400줄을 넘었고,
 * 이음매는 처음부터 여기였다 - 이 파일에는 `setTimeout`도 단계 전이도 없고, **불린 순간의 화면만** 만든다.
 *
 * ## 한 장이 열리는 순간도 여기 있다 (P5)
 *
 * 아홉 장이 미니 칩에서 큰 카드 자리로 옮겨 가며 "한 장을 연다"가 두 곳에서 일어나게 됐다(주인공 한 장과
 * 아홉 장 각각). 그래서 그 순간의 화면(`tearCard`·`playGrade`)을 여기 한 벌만 두고 지휘자가 두 자리에서
 * 같은 함수를 부른다 - 사본을 두면 소리 하나가 조용히 갈린다. **"언제"는 여전히 지휘자의 것**이고
 * 이 함수들은 불린 순간의 화면만 만든다.
 *
 * ## 상태를 갖지 않는다
 *
 * 지휘자의 클로저(뷰·연출 순서·현재 단계)를 `EnvelopeDrawState` 한 덩이로 받는다. 여기서 값을 기억하면
 * 같은 사실이 두 곳에 살고, 그때부터 화면과 단계가 어긋나기 시작한다.
 *
 * ## 지휘자의 타입만 `import type`으로 받는다
 *
 * `EnvelopeParts`(조각 묶음)는 지휘자의 것이라 그대로 쓴다. **타입만** 가져오므로 컴파일에서 지워지고
 * 런타임 고리는 생기지 않는다 - 조각 하나가 지휘자를 되부르는 것이 아니라, 지휘자가 둘로 갈린 것이다
 * (`envelopePhases.test.ts`의 `SIBLING_EDGES`가 그 하나를 목록으로 못박는다).
 *
 * ## 개봉 전에 등급을 앉히지 않는다
 *
 * 이 파일이 지켜야 하는 계약 하나: **카드 뒷면의 글자는 개봉 뒤에만 앉는다.** 언어를 바꿔 다시 그릴 때도
 * 마찬가지다 - 개봉 전에 등급 이름이 DOM에 있으면 뒷면이 안 보여도 그 자체가 유출이고,
 * `envelope.test.ts`의 "개봉 전은 네 등급의 DOM이 같다"가 그 자리를 대조로 지킨다.
 */

import { prefersReducedMotion } from '../core/env'
import { ENVELOPE_TABLE, gradeRank, type EnvelopeTable, type Grade } from '../game/economy/envelopeTable'
import { t } from '../i18n'

import { restartAnimation } from './dom'
import type { EnvelopeCardView } from './envelopeCard'
import { ceremonyFor, shardsFor, type GradeCeremony } from './envelopeCeremony'
import type { EnvelopeParts, EnvelopePhase, EnvelopeView } from './envelopePhases'

/* --- 타입 ---------------------------------------------------------------------- */

/** 그릴 순간의 지휘자 상태. 전부 읽기만 한다. */
export type EnvelopeDrawState = Readonly<{
  /** 없으면 화면이 접혀 있다는 뜻이다(문구만 비운다). */
  view: EnvelopeView | null
  /** `revealOrder`가 정한 순서. 마지막이 주인공이다. */
  ordered: readonly EnvelopeCardView[]
  phase: EnvelopePhase
}>

/* --- 순수 계산 ------------------------------------------------------------------ */

/** 이 뷰가 쓸 확률표. 뷰가 표를 들고 오지 않으면 기본 표다. */
export function tableOf(state: EnvelopeDrawState): EnvelopeTable {
  return state.view?.table ?? ENVELOPE_TABLE
}

/** 주인공 카드(한 장이면 그 한 장, 열 장이면 최고 등급). */
export function featureOf(state: EnvelopeDrawState): EnvelopeCardView | null {
  return state.ordered[state.ordered.length - 1] ?? null
}

/** 주인공을 뺀 나머지. 열 장 스택의 몫이다. */
export function lowerOf(state: EnvelopeDrawState): readonly EnvelopeCardView[] {
  return state.ordered.slice(0, Math.max(0, state.ordered.length - 1))
}

/** 이번 회차의 후반부 표. 움직임 줄이기면 등급과 무관하게 하나다. */
export function ceremonyPlanOf(state: EnvelopeDrawState): GradeCeremony {
  return ceremonyFor(featureOf(state)?.draw.grade ?? 'common', prefersReducedMotion())
}

/** 등급이 드러났는가. 카드 뒷면의 글자는 이 뒤에만 앉는다(개봉 전 유출 금지). */
export function isOpened(phase: EnvelopePhase): boolean {
  return phase === 'tear' || phase === 'stamp' || phase === 'done'
}

/** SR 이상인가. 크롭마크·저역 타격·등급 소리가 이 선 하나에서 갈린다. */
export function isRareOrBetter(grade: Grade): boolean {
  return gradeRank(grade) >= gradeRank('rare')
}

/* --- 그리기 (한 장이 열리는 순간) -------------------------------------------------- */

/**
 * 카드 한 장이 **찢기는 순간**. 주인공 한 장과 아홉 장 구간이 이 함수 하나를 함께 쓴다 -
 * 사본을 두면 "아홉 장은 소리가 하나 적다"처럼 갈라지고, 그 차이는 눈이 아니라 귀로 먼저 새어 나온다.
 *
 * 글자를 찢기 **전에** 앉히는 것이 순서의 핵심이다(개봉 전에는 뒷면이 비어 있었다) - 그래야 뒤집히는 면에
 * 글자가 실린다. 어두워짐(`dim`)과 피티·탄창 칩은 여기 없다: 앞의 것은 주인공만의 한 박자이고,
 * 뒤의 것은 회차 전체의 회계라 마지막 한 장이 도맡는다.
 *
 * `position`(몇 번째 장인가, 1부터)은 **지휘자가 세는 눈금을 그대로 받는다.** 여기서 `ordered.indexOf(entry)`로
 * 되찾지 않는 이유는 참조가 유일하지 않아서다 - 같은 산출물이 여러 장 나오면(열 장에서 흔하다) 그 장들이
 * 같은 객체일 수 있고, 그러면 여덟 장이 전부 "1 / 10"이 된다. 눈금은 이미 `stacked`에 있으므로 두 번 세지 않는다.
 */
export function tearCard(
  parts: EnvelopeParts,
  state: EnvelopeDrawState,
  entry: EnvelopeCardView,
  plan: GradeCeremony,
  position: number,
): void {
  const { card, ceremony, crops, play } = parts
  const grade = entry.draw.grade
  card.paintTexts(entry, position, state.ordered.length)
  card.tear(grade, plan.tearMs)
  ceremony.burst(grade, plan, shardsFor(entry.draw.seq, entry.draw.gradeRoll, plan.shards))

  // N은 찢는 소리를 쓰지 않는다. 봉인이 툭 벌어질 뿐이고, 그 초라함이 "후진 건 후지게"의 소리 쪽 절반이다.
  if (grade !== 'common') play('envelopeTear')
  play('cardFlip')
  /*
   * SR 이상에만 붙는 저역 타격(P5). **겹의 수가 곧 등급**이다 - SR은 여기서 한 겹, SSR은 금빛까지 두 겹,
   * 그리고 스탬프의 `gradeLegend`가 세 번째다. 아래 둘이 조용한 것이 위 둘의 값어치이므로 이 선을
   * `isRareOrBetter` 하나로 두고, 크롭마크가 켜지는 선과 **같은 함수**를 쓴다(선이 둘이면 언젠가 갈린다).
   */
  if (isRareOrBetter(grade)) play('envelopeImpact')
  if (plan.flare === 'gold') play('envelopeBurst')
  if (isRareOrBetter(grade)) crops.classList.add('is-flash')
}

/** 등급이 앉는 순간의 소리. N·R은 조용하다 - 그 조용함이 위 둘의 값어치다. */
export function playGrade(parts: EnvelopeParts, grade: Grade): void {
  if (grade === 'legend') parts.play('gradeLegend')
  else if (grade === 'rare') parts.play('gradeRare')
}

/* --- 그리기 (문구) --------------------------------------------------------------- */

/**
 * 게이지가 말할 피티. **출발점을 모르면 도착점도 그리지 않는다** - 게이지가 스탬프와 다른 말을 하게 된다.
 */
export function paintPity(parts: EnvelopeParts, state: EnvelopeDrawState, stage: 'before' | 'after'): void {
  const view = state.view
  const table = tableOf(state)
  if (view === null || view.pityBefore === null) {
    parts.gauge.paintPity(null, table)
    return
  }
  const after = featureOf(state)?.draw.pityAfter ?? view.pityBefore
  parts.gauge.paintPity(stage === 'before' ? view.pityBefore : after, table)
}

/** 언어에 따라 바뀌는 글자 전부. `show`와 언어 전환이 같은 함수를 쓴다. */
export function paintTexts(parts: EnvelopeParts, state: EnvelopeDrawState): void {
  const { hint, skip, gauge, head, actions, card, stack } = parts
  const view = state.view
  const table = tableOf(state)
  hint.textContent = t('envelope.skipHint')
  skip.textContent = t('envelope.skip')
  gauge.paintLegend(table)
  head.paintTexts(view === null ? null : { seq: featureOf(state)?.draw.seq ?? 0, replay: view.replay === true })
  actions.paintTexts(view === null ? null : { price: table.price, roundsToAfford: view.next.roundsToAfford })
  if (view === null) return

  // 카드 뒷면의 글자는 **개봉 뒤에만** 앉는다(언어를 바꿔도 마찬가지) - 개봉 전에 등급 이름이 DOM에 있으면
  // 그 자체가 유출이고, 어차피 뒷면은 뒤집히기 전까지 보이지 않는다.
  const main = featureOf(state)
  if (main !== null && isOpened(state.phase)) card.paintTexts(main, state.ordered.length, state.ordered.length)
  stack.paintTexts(lowerOf(state))
  paintPity(parts, state, state.phase === 'stamp' || state.phase === 'done' ? 'after' : 'before')
}

/* --- 그리기 (모양) --------------------------------------------------------------- */

/**
 * 열 장 구간에서 **다음 한 장을 위해 화면을 개봉 전으로 되돌린다**(P5).
 *
 * 되돌릴 것이 셋이다. 카드(등급·뒷면 글자·뒤집힘)와 크롭마크, 그리고 **세리머니 레이어**다. 셋째를 빠뜨렸던
 * 것이 이 화면의 고장이었다 - `is-burst`·`is-shake`·`data-ceremony`가 걷히지 않아 (1) 주인공의 개봉 전
 * 2.9초 동안 앞 장의 등급 색이 루트에 남고(그 자체가 유출이다), (2) `.ev-root.is-burst .ev-ceremony__dim`이
 * `is-dim` 규칙과 같은 특이도로 뒤에 있어 **주인공의 어두워짐이 통째로 사라졌다**, (3) 같은 클래스가 이미
 * 붙어 있어 다음 장의 빛·흔들림·파편이 다시 돌지 않았다.
 *
 * 그리고 **걷은 상태를 브라우저에 한 번 보여 준다**(`restartAnimation`). 이 함수가 돌아가는 즉시 같은
 * 태스크 안에서 `tearCard`가 같은 클래스를 도로 얹기 때문에, 리플로를 한 번 강제하지 않으면 브라우저가 두
 * 변경을 "바뀐 것 없음"으로 합쳐 보고 찢김·뒤집기·펀치·빛이 **한 번도** 다시 시작되지 않는다. 그러면 2~9번째
 * 장은 이미 뒤집힌 카드 위에서 글자만 툭툭 갈리고, jsdom에는 레이아웃이 없어 테스트는 전부 초록이다.
 * `.ev-root` 하나를 읽으면 그 아래(카드·크롭마크·세리머니)가 함께 플러시된다 - 세 번 읽을 이유가 없다.
 */
export function armCard(parts: EnvelopeParts): void {
  const { root, card, crops, ceremony } = parts
  card.reset()
  ceremony.reset()
  crops.classList.remove('is-flash')
  restartAnimation(root)
  card.rise()
}

/**
 * 화면을 첫 상태로 되돌린다. 봉투는 아래에, 바늘은 왼쪽 끝에, 릴은 출발 자리에, 카드는 앉힌 채로.
 *
 * **등급은 아무 데도 없다.** 릴의 출발 자리는 상수라 이 순간의 트랙이 네 등급 모두 같고
 * (`envelopeReel.ts` 머리말), 카드의 `data-grade`와 뒷면 글자는 찢는 순간에 처음 앉는다.
 */
export function resetVisual(parts: EnvelopeParts, state: EnvelopeDrawState): void {
  const { root, crops, hint, skip, head, gauge, stack, card, ceremony, actions } = parts
  const view = state.view
  if (view === null) return
  const main = featureOf(state)

  root.classList.remove('is-done')
  card.reset()
  gauge.resetNeedle()
  gauge.armReel(tableOf(state), main?.draw.gradeRoll ?? 0, main?.draw.seq ?? 0)
  stack.reset(lowerOf(state))
  ceremony.reset()
  crops.classList.remove('is-flash')
  actions.hide()
  hint.hidden = false
  // 건너뛰기는 연출이 도는 동안에만 선다. 끝나면 `enterDone`이 걷는다 - 서 있는 자리가 한 곳, 걷는 자리가 한 곳이다.
  skip.hidden = false

  // 칩은 계산하지 않는다(`pinsChip.ts` 머리말) - 출발값도 받은 것을 그대로 놓는다.
  head.pins.set(view.pinsBefore)
  // 뺄 것은 **이번 회차 전체 지급분**이다(주인공 한 장만 빼면 낮은 등급 카드가 준 탄창이 빠져 칩이 처음부터 끝값이다).
  const granted = state.ordered.reduce((sum, entry) => sum + entry.magazinesGranted, 0)
  head.resetMagazines(Math.max(0, view.magazinesAfter - granted))
}
